// PersonNode — stateless SVG group for a single person in the org graph.
//
// Renders, at the supplied (x, y):
//   - an optional outer halo ring when isCurrentUser is true
//   - the main circle filled with `color` (a CSS variable or hex provided by
//     the integrator — colour assignment lives in US6, not here)
//   - the person's initials, centred
//   - a truncated display-name label beneath the circle
//   - an optional "External" badge when isExternal is true
//
// All sizing is fixed and the component owns zero state. Positioning is the
// caller's responsibility (US3's simulation hook).

import type { IOrgGraphNode } from '@skystone/shared';
import { cn } from '@/lib/utils';
import { initialsOf, truncateLabel } from './utils';

const REGULAR_RADIUS = 24;
const CURRENT_USER_RADIUS = 32;
const HALO_PADDING = 6;
const LABEL_OFFSET = 14;
const BADGE_OFFSET = 28;

interface PersonNodeProps {
  /** The node data — shape comes from `@skystone/shared`. */
  node: IOrgGraphNode;
  /** Simulated x position (logical SVG coords). */
  x: number;
  /** Simulated y position (logical SVG coords). */
  y: number;
  /** Department fill colour (CSS variable name or hex). US6 owns the map. */
  color?: string;
  /** Optional extra className for the wrapping `<g>`. */
  className?: string;
  /**
   * Whether to render the display-name label beneath the circle. Defaults to
   * `false` so dense graphs don't suffer from label overlap; the integrator
   * flips it `true` for the focused/hovered node. The full name remains
   * available via the SVG `<title>` and via the wrapping `aria-label` so
   * screen-reader users are unaffected.
   */
  showLabel?: boolean;
}

export function PersonNode({
  node,
  x,
  y,
  color = 'var(--color-muted)',
  className,
  showLabel = false,
}: Readonly<PersonNodeProps>) {
  const radius = node.isCurrentUser ? CURRENT_USER_RADIUS : REGULAR_RADIUS;
  const initials = initialsOf(node);
  const truncated = truncateLabel(node.displayName);
  const wasTruncated = truncated !== node.displayName;

  return (
    <g
      transform={`translate(${x}, ${y})`}
      className={cn('org-graph-person', className)}
      data-testid="person-node"
      data-upn={node.id}
    >
      {/* Full name available via SVG <title> for browser tooltip + screen readers */}
      <title>{node.displayName}</title>

      {node.isCurrentUser && (
        <circle
          r={radius + HALO_PADDING}
          fill="none"
          stroke={color}
          strokeWidth={2}
          strokeOpacity={0.4}
          data-testid="person-node-halo"
        />
      )}

      <circle
        r={radius}
        fill={color}
        stroke="var(--color-border)"
        strokeWidth={1}
        data-testid="person-node-circle"
      />

      <text
        textAnchor="middle"
        dominantBaseline="central"
        className="select-none fill-foreground text-sm font-semibold"
        aria-hidden="true"
      >
        {initials}
      </text>

      {showLabel && (
        <text
          y={radius + LABEL_OFFSET}
          textAnchor="middle"
          className="select-none fill-foreground text-[11px]"
          data-testid="person-node-label"
        >
          {truncated}
          {wasTruncated && <title>{node.displayName}</title>}
        </text>
      )}

      {node.isExternal && (
        <g transform={`translate(0, ${radius + BADGE_OFFSET})`} data-testid="person-node-external-badge">
          <rect
            x={-26}
            y={-7}
            width={52}
            height={14}
            rx={4}
            fill="var(--color-warning)"
            fillOpacity={0.15}
            stroke="var(--color-warning)"
            strokeWidth={0.5}
          />
          <text
            textAnchor="middle"
            dominantBaseline="central"
            className="select-none fill-warning text-[10px] font-medium uppercase tracking-wide"
          >
            External
          </text>
        </g>
      )}
    </g>
  );
}
